import {
  mdiChatOutline,
  mdiCreation,
  mdiImageSearch,
  mdiInvoiceTextOutline,
  mdiRobotOutline,
  mdiTextBoxOutline,
} from '@mdi/js';

/**
 * The kinds of AI usage the admin view knows, one entry per kind the API's
 * usage report files a line under (`usage-report.ts` `kindOf`).
 *
 * The one place a kind is described. `summariseReport` builds each kind's
 * totals and company table from it; the page builds the feature tab's
 * sub-views and overview rows, the key-figure tiles, the token and cost
 * splits and the partial-cost note from it. A new kind is one entry here and
 * its locale keys — nothing on the page enumerates kinds by hand.
 *
 * Every label is `[key, englishDefault]`, for `t(...entry)`.
 *
 * @typedef {Object} UsageKind
 * @property {string} id the report's `kind`
 * @property {[string, string]} label the kind's name
 * @property {[string, string]} kpiLabel the key-figure tile's title
 * @property {[string, string]} unit what one `turns` is: a chat turn, an
 *   image tagged, a scan run… — the count column's header
 * @property {string} iconPath
 * @property {boolean} byPeople rows carry the ids of the people who used it,
 *   so a users count means something; a `SYSTEM:` actor's kind has none
 * @property {boolean} charged the caller's company pays, through its licence
 *   or the free tier — the writers that put a `chargedBusinessId` on their
 *   usage rows; the licence figures count these and no others
 * @property {?('toolCalls'|'tagsAdded'|'modelCalls')} extra the figure the
 *   kind's company table shows beside the count
 * @property {boolean} kpi a tile of its own among the key figures; the
 *   feature overview lists every kind regardless
 */

/** @type {UsageKind[]} */
export const USAGE_KINDS = [
  {
    id: 'chat',
    label: ['admin_ai.kind_chat', 'Chat'],
    kpiLabel: ['admin_ai.kpi_chat_turns', 'Chat turns'],
    unit: ['admin_ai.col_turns', 'Turns'],
    iconPath: mdiChatOutline,
    byPeople: true,
    charged: true,
    extra: 'toolCalls',
    kpi: true,
  },
  {
    id: 'tagging',
    label: ['admin_ai.kind_tagging', 'Image tagging'],
    kpiLabel: ['admin_ai.kpi_images_tagged', 'Images tagged'],
    unit: ['admin_ai.col_images', 'Images'],
    iconPath: mdiImageSearch,
    byPeople: false,
    charged: false,
    extra: 'tagsAdded',
    kpi: false,
  },
  {
    // A run is one lambda invocation for one model; an invoice that needed
    // two models counts twice.
    id: 'invoices',
    label: ['admin_ai.kind_invoices', 'Invoice scanning'],
    kpiLabel: ['admin_ai.kpi_invoice_runs', 'Invoice scan runs'],
    unit: ['admin_ai.col_runs', 'Scan runs'],
    iconPath: mdiInvoiceTextOutline,
    byPeople: false,
    charged: false,
    extra: 'modelCalls',
    kpi: false,
  },
  {
    // A run is one scheduled turn of the assistant.
    id: 'automations',
    label: ['admin_ai.kind_automations', 'Scheduled tasks'],
    kpiLabel: ['admin_ai.kpi_automation_runs', 'Scheduled task runs'],
    unit: ['admin_ai.col_automation_runs', 'Runs'],
    iconPath: mdiRobotOutline,
    byPeople: false,
    charged: true,
    extra: 'toolCalls',
    kpi: false,
  },
  {
    // One turn is one call whose notes were written; a long call takes
    // several model calls (summarised in parts and merged).
    id: 'meeting_notes',
    label: ['admin_ai.kind_meeting_notes', 'Meeting notes'],
    kpiLabel: ['admin_ai.kpi_meeting_notes', 'Calls with notes'],
    unit: ['admin_ai.col_calls_noted', 'Calls'],
    iconPath: mdiTextBoxOutline,
    byPeople: false,
    charged: true,
    extra: 'modelCalls',
    kpi: false,
  },
  {
    // The document bank's search box asking the photo search through a
    // small model: one turn per search, under the person who searched.
    id: 'photo_search',
    label: ['admin_ai.kind_photo_search', 'Photo search'],
    kpiLabel: ['admin_ai.kpi_photo_searches', 'Photo searches'],
    unit: ['admin_ai.col_searches', 'Searches'],
    iconPath: mdiCreation,
    byPeople: true,
    charged: true,
    extra: null,
    kpi: false,
  },
];

export const USAGE_KIND_IDS = USAGE_KINDS.map((kind) => kind.id);

/** The kind by its id, or undefined for one this build does not know. */
export const usageKindById = (id) => USAGE_KINDS.find((kind) => kind.id === id);

/** The ids whose lines a licence or the free-tier allowance pays for. */
export const chargedUsageKindIds = () =>
  USAGE_KINDS.filter((kind) => kind.charged).map((kind) => kind.id);
